/* Super Feels v2 — site behaviour.
   No dependencies. Every module no-ops when its markup is absent. */
(() => {
  'use strict';
  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------
     GLOBAL PROMOTIONAL LIMITS & AUTO-DISCOUNT APP INITIALIZATION
     --------------------------------------------------------- */
  window.SF_PROMO_CONFIG = window.SF_PROMO_APP_SETTINGS?.shopMetafieldConfig || null;
  window.SF_PROMO_VERIFIED = sessionStorage.getItem('sf_promo_verified') === 'true';
  window.SF_PROMO_DISCOUNT_CODE = sessionStorage.getItem('sf_promo_discount') || localStorage.getItem('sf_promo_discount') || null;

  const PROMO_WORKER_URL = window.SF_PROMO_APP_SETTINGS?.workerUrl || 'https://superfeels-variant-limits.shopifyd335.workers.dev';

  const fetchPromoConfig = async () => {
    if (window.SF_PROMO_APP_SETTINGS?.enabled === false) return;
    try {
      const res = await fetch(`${PROMO_WORKER_URL}/api/config`).then(r => r.json());
      if (res && res.success && res.config) {
        window.SF_PROMO_CONFIG = { ...window.SF_PROMO_CONFIG, ...res.config };
        window.dispatchEvent(new CustomEvent('sf:promo-config-updated', { detail: window.SF_PROMO_CONFIG }));
      }
    } catch (e) {
      console.warn('Promo config fetch error:', e);
    }
  };
  fetchPromoConfig();

  /* ---------------------------------------------------------
     AGE GATE — honour check, localStorage flag.
     Not identity verification; a regulated market needs a vendor.
     --------------------------------------------------------- */
  (() => {
    const gate = $('#ageGate');
    if (!gate) return;
    if (gate.dataset.enabled === 'false') return;
    const KEY = 'sf_age_ok';
    if (localStorage.getItem(KEY) === '1') return;

    const card = $('.gate__card', gate);
    const deny = $('#gateDeny');
    gate.hidden = false;
    document.body.style.overflow = 'hidden';
    const focusables = () => $$('button, [href], input', gate).filter(el => el.offsetParent !== null);
    setTimeout(() => (focusables()[0] || gate).focus(), 60);

    $('#gateYes').addEventListener('click', () => {
      try { localStorage.setItem(KEY, '1'); } catch (e) {}
      gate.hidden = true;
      document.body.style.overflow = '';
    });
    $('#gateNo').addEventListener('click', () => {
      card.hidden = true;
      deny.hidden = false;
      setTimeout(() => $('#gateBack')?.focus(), 40);
    });
    // a mis-tap on "No" used to be an unrecoverable dead end
    $('#gateBack')?.addEventListener('click', () => {
      deny.hidden = true;
      card.hidden = false;
      setTimeout(() => $('#gateYes').focus(), 40);
    });
    // trap focus while the gate is up
    gate.addEventListener('keydown', e => {
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  })();

  /* ---------------------------------------------------------
     ANNOUNCEMENT BAR — rotating ticker + dismissible
     --------------------------------------------------------- */
  (() => {
    const bar = $('#announcementBar');
    if (!bar) return;

    const DISMISS_KEY = 'sf_announce_closed';
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') {
        bar.style.display = 'none';
        return;
      }
    } catch (e) {}

    const closeBtn = $('#announceClose', bar);
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        bar.classList.add('is-dismissed');
        try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) {}
        setTimeout(() => { bar.style.display = 'none'; }, 300);
      });
    }

    const items = $$('.announce__track:not(.announce__track--clone) .announce__item', bar);
    if (items.length <= 1) return;

    let currentIndex = 0;
    let timer = null;
    const speed = parseInt(bar.dataset.speed, 10) || 4000;

    const showSlide = nextIndex => {
      items[currentIndex].classList.remove('is-active');
      currentIndex = (nextIndex + items.length) % items.length;
      items[currentIndex].classList.add('is-active');
    };

    const startTimer = () => {
      if (timer || REDUCED || window.innerWidth <= 640) return;
      timer = setInterval(() => {
        showSlide(currentIndex + 1);
      }, speed);
    };

    const stopTimer = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    startTimer();

    window.addEventListener('resize', () => {
      if (window.innerWidth <= 640) stopTimer();
      else if (!timer) startTimer();
    }, { passive: true });

    // Pause on hover or focus so user can read and interact
    bar.addEventListener('mouseenter', stopTimer);
    bar.addEventListener('mouseleave', startTimer);
    bar.addEventListener('focusin', stopTimer);
    bar.addEventListener('focusout', startTimer);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopTimer();
      else startTimer();
    });
  })();

  /* ---------------------------------------------------------
     NAV — mobile drawer (smooth slide from left) + keyboard-openable mega menus
     --------------------------------------------------------- */
  (() => {
    const burger = $('#burger'), drawer = $('#drawer');
    const closeBtn = $('#drawerCloseBtn'), overlay = $('#drawerOverlay');

    if (burger && drawer) {
      let closeTimeout = null;

      const openNav = () => {
        if (closeTimeout) clearTimeout(closeTimeout);
        drawer.hidden = false;
        void drawer.offsetWidth;
        drawer.classList.add('is-open');
        burger.setAttribute('aria-expanded', 'true');
        burger.setAttribute('aria-label', 'Close menu');
        document.body.style.overflow = 'hidden';
      };

      const closeNav = () => {
        drawer.classList.remove('is-open');
        burger.setAttribute('aria-expanded', 'false');
        burger.setAttribute('aria-label', 'Open menu');
        document.body.style.overflow = '';
        if (closeTimeout) clearTimeout(closeTimeout);
        closeTimeout = setTimeout(() => {
          if (!drawer.classList.contains('is-open')) {
            drawer.hidden = true;
          }
        }, 360);
      };

      burger.addEventListener('click', () => {
        if (drawer.classList.contains('is-open')) {
          closeNav();
        } else {
          openNav();
        }
      });

      closeBtn?.addEventListener('click', closeNav);
      overlay?.addEventListener('click', closeNav);

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && drawer && !drawer.hidden && drawer.classList.contains('is-open')) {
          closeNav();
        }
      });

      // Close drawer when navigating to internal hash links
      $$('#drawer a[href^="#"]').forEach(a => {
        a.addEventListener('click', () => {
          closeNav();
        });
      });
    }

    // mega menus open on hover via CSS; add click/Escape for touch + keyboard
    $$('.hdr__item.has-mega').forEach(item => {
      const top = $('.hdr__top', item);
      top.addEventListener('click', e => {
        if (matchMedia('(hover: hover)').matches) return;  // let the link work on desktop
        if (!item.classList.contains('is-open')) {
          e.preventDefault();
          $$('.hdr__item.has-mega').forEach(o => o !== item && o.classList.remove('is-open'));
          item.classList.add('is-open');
          top.setAttribute('aria-expanded', 'true');
        }
      });
    });
    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      $$('.hdr__item.has-mega.is-open').forEach(i => {
        i.classList.remove('is-open');
        $('.hdr__top', i).setAttribute('aria-expanded', 'false');
      });
    });
    document.addEventListener('click', e => {
      if (e.target.closest('.hdr__item.has-mega')) return;
      $$('.hdr__item.has-mega.is-open').forEach(i => i.classList.remove('is-open'));
    });
  })();

  /* ---------------------------------------------------------
     HEADER — sticky compact on scroll
     --------------------------------------------------------- */
  (() => {
    const hdr = $('#hdr');
    if (!hdr) return;
    const onScroll = () => {
      const scrolled = window.scrollY > 20;
      hdr.classList.toggle('is-scrolled', scrolled);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  })();

  /* ---------------------------------------------------------
     GET $20 — opens / focuses email capture section
     --------------------------------------------------------- */
  (() => {
    const triggerEmailCapture = e => {
      e?.preventDefault();
      const drawer = $('#drawer');
      const burger = $('#burger');
      if (drawer && !drawer.hidden) {
        drawer.classList.remove('is-open');
        if (burger) {
          burger.setAttribute('aria-expanded', 'false');
          burger.setAttribute('aria-label', 'Open menu');
        }
        document.body.style.overflow = '';
        setTimeout(() => {
          if (!drawer.classList.contains('is-open')) {
            drawer.hidden = true;
          }
        }, 360);
      }
      const eband = $('#emailCapture');
      const input = $('#ebandEmail');
      if (eband) {
        eband.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' });
        if (input) {
          setTimeout(() => {
            input.focus();
            input.classList.remove('is-highlighted');
            void input.offsetWidth;
            input.classList.add('is-highlighted');
          }, 350);
        }
      }
    };

    $('#get20Btn')?.addEventListener('click', triggerEmailCapture);
    $('#drawerGet20Btn')?.addEventListener('click', triggerEmailCapture);
  })();

  /* ---------------------------------------------------------
     CART DRAWER & LIVE CART STATE
     --------------------------------------------------------- */
  (() => {
    const drawer = $('#cartDrawer');
    const badge  = $('#cartBadge');
    const btn    = $('#cartBtn');
    const close  = $('#cartCloseBtn');
    const over   = $('#cartOverlay');
    const list   = $('#cartItemsList');
    const countEl = $('#cartDrawerCount');
    const subtotalEl = $('#cartSubtotal');
    const foot   = $('#cartFoot');
    const shipText = $('#shippingText');
    const shipProg = $('#shippingProgress');

    const CART_KEY = 'sf_cart_data';
    const money = n => '$' + n.toFixed(2);

    const loadCart = () => {
      try {
        const raw = localStorage.getItem(CART_KEY);
        if (raw) return JSON.parse(raw);
      } catch (e) {}
      return [];
    };

    const saveCart = cart => {
      try {
        localStorage.setItem(CART_KEY, JSON.stringify(cart));
      } catch (e) {}
    };


    /* ---- Shopify cart bridge --------------------------------------------
       The drawer above renders from a plain array of
       {key, id, title, plan, price, qty, frequency, img}. These helpers keep
       that array in sync with Shopify's real cart, so the UI is unchanged but
       the line items are the ones that reach checkout. */
    const sfMapLine = l => ({
      key: l.key,
      id: l.variant_id,
      title: l.product_title,
      plan: l.selling_plan_allocation
              ? l.selling_plan_allocation.selling_plan.name
              : (l.variant_title || ''),
      price: l.final_price / 100,
      qty: l.quantity,
      img: l.image
    });

    const sfRefresh = async (bump = false) => {
      try {
        const data = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
        cart = (data.items || []).map(sfMapLine);
      } catch (e) {
        cart = [];
      }
      updateUI(bump);
    };

    /* High-performance direct single-request cart line modifier with instant optimistic UI update */
    const sfChangeLine = async (key, targetQty) => {
      // 1. Instant optimistic DOM update (0ms delay)
      const lineIdx = cart.findIndex(c => c.key === key);
      if (lineIdx !== -1) {
        if (targetQty <= 0) {
          cart.splice(lineIdx, 1);
        } else {
          cart[lineIdx].qty = targetQty;
        }
        updateUI(false);
      }

      // 2. Single background sync to Shopify Cart API
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: Math.max(0, targetQty) })
        });
        if (res.ok) {
          const data = await res.json();
          cart = (data.items || []).map(sfMapLine);
          updateUI(false);
          // Trigger prefetch in background if cart contents changed
          triggerDiscountPrefetch();
        } else {
          await sfRefresh(false);
        }
      } catch (e) {
        await sfRefresh(false);
      }
    };

    let cart = [];

    const updateUI = (bump = false) => {
      const totalCount = cart.reduce((sum, item) => sum + item.qty, 0);
      const totalPrice = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);

      // Update header badge
      if (badge) {
        badge.textContent = String(totalCount);
        badge.hidden = totalCount === 0;
        if (bump) {
          badge.classList.remove('is-bumped');
          void badge.offsetWidth;
          badge.classList.add('is-bumped');
        }
      }
      if (btn) {
        btn.setAttribute('aria-label', `Cart (${totalCount} item${totalCount === 1 ? '' : 's'})`);
      }
      if (countEl) countEl.textContent = String(totalCount);

      // Shipping progress towards $19.99
      const THRESHOLD = (typeof window.SF_FREE_SHIPPING_THRESHOLD === 'number' && window.SF_FREE_SHIPPING_THRESHOLD > 0)
        ? window.SF_FREE_SHIPPING_THRESHOLD
        : 19.99;
      if (shipText && shipProg) {
        if (totalPrice >= THRESHOLD && totalCount > 0) {
          shipText.innerHTML = `<b>You've unlocked FREE standard shipping!</b>`;
          shipProg.style.width = '100%';
        } else if (totalCount > 0) {
          const rem = THRESHOLD - totalPrice;
          shipText.innerHTML = `Add <b>${money(rem)}</b> more for <b>FREE SHIPPING</b>`;
          shipProg.style.width = `${Math.min(100, (totalPrice / THRESHOLD) * 100)}%`;
        } else {
          shipText.innerHTML = `Free shipping on orders over $19.99`;
          shipProg.style.width = '0%';
        }
      }

      // Render items
      if (!list) return;
      const basePrefix = '';   // Shopify routes are absolute

      if (cart.length === 0) {
        list.innerHTML = `
          <div class="cart-empty">
            <div class="cart-empty__icon">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 8h12l-1 12H7L6 8z"/><path d="M9 8V6a3 3 0 016 0v2"/></svg>
            </div>
            <h3 class="cart-empty__h">Your cart is empty</h3>
            <p class="cart-empty__p">Feel the shift. Every bottle is third-party lab tested with active milligrams printed on the label.</p>
            <a class="btn btn--solid btn--wide" href="/products/super-feels" id="cartShopBtn">SHOP SUPER FEELS</a>
          </div>
        `;
        if (foot) foot.hidden = true;
      } else {
        let discountedSubtotal = 0;
        list.innerHTML = cart.map((item, idx) => {
          const isPromo = window.SF_PROMO_CONFIG && 
                          String(item.id).replace(/\D/g, '') === String(window.SF_PROMO_CONFIG.variantId).replace(/\D/g, '') && 
                          (window.SF_PROMO_VERIFIED || localStorage.getItem('sf_promo_discount') || sessionStorage.getItem('sf_promo_verified') === 'true');
          const promoPrice = isPromo && window.SF_PROMO_CONFIG.promoPrice ? window.SF_PROMO_CONFIG.promoPrice : item.price;
          const regPrice = isPromo && window.SF_PROMO_CONFIG.regularPrice ? window.SF_PROMO_CONFIG.regularPrice : item.price;
          discountedSubtotal += promoPrice * item.qty;

          return `
          <div class="cart-item" data-id="${item.id}">
            <div class="cart-item__img">
              <img src="${item.img || (window.SF_FALLBACK_IMG || '')}" alt="${item.title}">
            </div>
            <div class="cart-item__info">
              <div class="cart-item__title">${item.title}</div>
              ${isPromo ? `<div style="font-size:11px;font-weight:700;color:#108043;margin-top:2px;">Special Offer Applied (-$${((regPrice - promoPrice) * item.qty).toFixed(2)})</div>` : ''}
              ${item.plan ? `<span class="cart-item__plan">${item.plan}${item.frequency ? ' &middot; ' + item.frequency : ''}</span>` : ''}
              <div class="cart-item__controls">
                <button type="button" class="cart-item__qty-btn" data-action="dec" data-idx="${idx}" aria-label="Decrease quantity">&minus;</button>
                <span class="cart-item__qty">${item.qty}</span>
                <button type="button" class="cart-item__qty-btn" data-action="inc" data-idx="${idx}" aria-label="Increase quantity">&plus;</button>
              </div>
            </div>
            <div class="cart-item__right">
              ${isPromo ? `<span class="cart-item__price" style="color:#108043;"><s style="color:#6d7175;font-weight:normal;margin-right:4px;">${money(regPrice * item.qty)}</s>${money(promoPrice * item.qty)}</span>` : `<span class="cart-item__price">${money(item.price * item.qty)}</span>`}
              <button type="button" class="cart-item__del" data-action="del" data-idx="${idx}">Remove</button>
            </div>
          </div>
        `;
        }).join('');
        if (subtotalEl) {
          if (discountedSubtotal < totalPrice) {
            subtotalEl.innerHTML = `<s style="color:#8F97A3;font-size:14px;font-weight:normal;margin-right:6px;">${money(totalPrice)}</s><span style="color:#108043;">${money(discountedSubtotal)}</span>`;
          } else {
            subtotalEl.textContent = money(totalPrice);
          }
        }
        if (foot) foot.hidden = false;
      }
    };

    const openDrawer = () => {
      if (!drawer) return;
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
      if (btn) btn.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
      });
      setTimeout(() => $('#cartCloseBtn')?.focus(), 50);
    };

    const closeDrawer = () => {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      setTimeout(() => {
        drawer.hidden = true;
      }, 350);
    };

    btn?.addEventListener('click', () => {
      if (drawer.classList.contains('is-open')) closeDrawer();
      else openDrawer();
    });

    close?.addEventListener('click', closeDrawer);
    over?.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && drawer && !drawer.hidden && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // Cart list interactions (quantity inc/dec/delete) with instant response
    list?.addEventListener('click', e => {
      const target = e.target.closest('button');
      if (!target) return;
      const action = target.dataset.action;
      const idx = parseInt(target.dataset.idx, 10);
      if (isNaN(idx) || !cart[idx]) return;

      const item = cart[idx];
      if (action === 'inc') {
        const item = cart[idx];
        const isPromoItem = window.SF_PROMO_CONFIG && String(item.id).replace(/\D/g, '') === String(window.SF_PROMO_CONFIG.variantId).replace(/\D/g, '');
        const maxUnits = window.SF_PROMO_CONFIG?.maxLifetimeUnits || 2;
        if (isPromoItem && item.qty >= maxUnits) {
          alert(`Limit ${maxUnits} per customer for this promotional item.`);
          return;
        }
        sfChangeLine(item.key, item.qty + 1);
      } else if (action === 'dec') {
        sfChangeLine(cart[idx].key, cart[idx].qty - 1);
      } else if (action === 'del') {
        sfChangeLine(cart[idx].key, 0);
      }
    });

    // Background Discount Prefetching so Checkout Redirect is Instantaneous (0ms)
    let precomputedDiscountCode = null;
    let isPrefetchingDiscount = false;

    const triggerDiscountPrefetch = async () => {
      if (!cart || cart.length === 0 || isPrefetchingDiscount) return;
      const cartIds = cart.map(item => item.id || item.variantId).filter(Boolean);
      if (cartIds.length === 0) return;

      isPrefetchingDiscount = true;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2500);

        const emailToVerify = sessionStorage.getItem('sf_promo_email') || window.SF_CUSTOMER_EMAIL || '';
        const res = await fetch(`${PROMO_WORKER_URL}/api/check-eligibility`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: emailToVerify,
            variantIds: cartIds
          }),
          signal: controller.signal
        }).then(r => r.json());
        clearTimeout(timeoutId);

        if (res.eligible && res.discountCode) {
          precomputedDiscountCode = res.discountCode;
          window.SF_PROMO_DISCOUNT_CODE = res.discountCode;
        } else {
          precomputedDiscountCode = null;
        }
      } catch (e) {
        precomputedDiscountCode = null;
      } finally {
        isPrefetchingDiscount = false;
      }
    };

    // Intercept checkout: Instant redirect if precomputed code exists or no promo in cart
    const checkoutBtn = $('.cart-drawer__checkout', drawer);
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', async e => {
        if (!cart || cart.length === 0) return;

        // If customer is verified and dynamic discount code is ready, redirect with discount code
        const isVerified = window.SF_PROMO_VERIFIED || sessionStorage.getItem('sf_promo_verified') === 'true';
        const promoCode = isVerified ? (window.SF_PROMO_DISCOUNT_CODE || localStorage.getItem('sf_promo_discount') || sessionStorage.getItem('sf_promo_discount') || precomputedDiscountCode) : null;
        if (promoCode && isVerified) {
          e.preventDefault();
          window.location.href = `/discount/${encodeURIComponent(promoCode)}?redirect=/checkout`;
          return;
        }

        // If customer is not promo-verified, proceed directly to regular checkout without discount
        if (!isVerified) {
          window.location.href = '/checkout';
          return;
        }

        // If cart has no items or no prefetch yet, verify quickly with a max 1.2s timeout
        e.preventDefault();
        const origText = checkoutBtn.innerHTML;
        checkoutBtn.innerHTML = 'Proceeding...';
        checkoutBtn.style.pointerEvents = 'none';

        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 1200);

          const cartIds = cart.map(item => item.id || item.variantId).filter(Boolean);
          const emailToVerify = sessionStorage.getItem('sf_promo_email') || window.SF_CUSTOMER_EMAIL || '';
          const res = await fetch(`${PROMO_WORKER_URL}/api/check-eligibility`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: emailToVerify,
              variantIds: cartIds
            }),
            signal: controller.signal
          }).then(r => r.json());
          clearTimeout(timeoutId);

          if (res.eligible && res.discountCode) {
            window.location.href = `/discount/${encodeURIComponent(res.discountCode)}?redirect=/checkout`;
            return;
          }

          if (res.hasPromoVariant && !res.eligible && res.message) {
            alert(res.message);
          }

          window.location.href = '/checkout';
        } catch (err) {
          // If timeout or network error, proceed immediately to checkout without hanging
          window.location.href = '/checkout';
        }
      });
    }

    // Global Add To Cart method -> Instant drawer opening + Shopify Cart AJAX API
    window.sfAddToCart = item => {
      const formVariant = document.querySelector('#pdpVariantId, form[action*="/cart/add"] [name="id"]');
      const rawVariantId = item.variantId || (formVariant?.value && !isNaN(Number(formVariant.value)) ? formVariant.value : null) || item.id || window.SF_CURRENT_VARIANT_ID;
      if (!rawVariantId || isNaN(Number(rawVariantId))) {
        console.warn('No valid variant ID found, opening drawer', item);
        openDrawer();
        return;
      }

      // Enforce limit per customer for promotional item
      const isPromo = window.SF_PROMO_CONFIG && String(rawVariantId).replace(/\D/g, '') === String(window.SF_PROMO_CONFIG.variantId).replace(/\D/g, '');
      const maxUnits = window.SF_PROMO_CONFIG?.maxLifetimeUnits || 2;
      if (isPromo) {
        const inCart = (cart || []).find(c => String(c.id).replace(/\D/g, '') === String(rawVariantId).replace(/\D/g, ''));
        const totalAfter = (inCart ? inCart.qty : 0) + (item.qty || 1);
        if (totalAfter > maxUnits) {
          alert(`Limit ${maxUnits} per customer for this promotional item.`);
          openDrawer();
          return;
        }
      }

      // OPEN DRAWER INSTANTLY ON CLICK (0ms perceived lag)
      openDrawer();

      const body = { id: Number(rawVariantId), quantity: item.qty || 1 };

      // Attach selling plan ONLY if item is explicitly a subscription (item.isSub === true)
      if (item.isSub === true && item.sellingPlan && !isNaN(Number(item.sellingPlan)) && Number(item.sellingPlan) > 1000) {
        body.selling_plan = Number(item.sellingPlan);
      }

      // Run background add-to-cart API call
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(async res => {
          const data = await res.json();
          if (!res.ok) {
            console.error('Shopify /cart/add.js error:', res.status, data);
            alert(data.description || data.message || 'Unable to add to cart. Please choose an option.');
            await sfRefresh(false);
            return;
          }
          await sfRefresh(true);
          triggerDiscountPrefetch();
        })
        .catch(async err => {
          console.error('Network error adding to cart:', err);
          await sfRefresh(false);
        });
    };

    sfRefresh();
  })();

  /* ---------------------------------------------------------
     INGREDIENT ORBIT — hover/focus a plant, read its dose + farm story.
     The reference site does not do this; it is the upgrade.
     Dots are laid out on a circle from their data-angle.
     --------------------------------------------------------- */
  (() => {
    const orbit = $('.ing__orbit');
    if (!orbit) return;
    const dots  = $$('.ing__dot', orbit);
    const panes = $$('.ing__pane');
    if (!dots.length) return;

    // place each dot on the ring
    const layout = () => {
      const r = 43;                                  // % of container
      dots.forEach(d => {
        const a = (parseFloat(d.dataset.angle) - 90) * Math.PI / 180;
        d.style.left = `${50 + r * Math.cos(a)}%`;
        d.style.top  = `${50 + r * Math.sin(a)}%`;
      });
    };
    layout();
    addEventListener('resize', layout, { passive: true });

    const show = key => {
      dots.forEach(d => d.classList.toggle('is-on', d.dataset.ing === key));
      panes.forEach(p => p.classList.toggle('is-on', p.dataset.ing === key));
    };
    dots.forEach(d => {
      const go = () => show(d.dataset.ing);
      d.addEventListener('mouseenter', go);
      d.addEventListener('focus', go);
      d.addEventListener('click', go);
    });
    show(dots[0].dataset.ing);
  })();

  /* ---------------------------------------------------------
     PDP BUY BOX — Harmonized pricing, stepper, and cart connection
     --------------------------------------------------------- */
  (() => {
    const box = $('[data-buybox]');
    if (!box) return;
    const sizes = $$('.size__opt', box);
    const opts  = $$('.opt__row', box);
    const money = n => '$' + n.toFixed(2);

    let currentQty = 1;
    const qtyDisplay = $('#pdpQtyDisplay');
    const qtyMinus = $('#pdpQtyMinus');
    const qtyPlus = $('#pdpQtyPlus');
    const mainBuyBtn = $('#pdpAddToCart');
    const servingHint = $('#pdpServingHint');
    const bar = $('#buybar');
    const buybarName = $('#buybarName');
    const buybarPrice = $('#buybarPrice');
    const buybarBtn = $('#buybarAddToCart');

    if (qtyMinus && qtyPlus && qtyDisplay) {
      qtyMinus.addEventListener('click', () => {
        if (currentQty > 1) {
          currentQty--;
          qtyDisplay.textContent = String(currentQty);
          const qtyInput = $('#pdpQtyInput');
          if (qtyInput) qtyInput.value = String(currentQty);
          render();
        }
      });
      qtyPlus.addEventListener('click', () => {
        const size = sizes.find(s => s.classList.contains('is-on')) || sizes[0];
        const count = parseInt(size?.dataset.count || '1', 10);
        if (count === 3 && currentQty >= 2) {
          alert('Limit 2 per order for 3-Pack Case.');
          return;
        }
        currentQty++;
        qtyDisplay.textContent = String(currentQty);
        const qtyInput = $('#pdpQtyInput');
        if (qtyInput) qtyInput.value = String(currentQty);
        render();
      });
    }

    const is501ClubPDP = () => {
      const pathname = (window.location.pathname || '').toLowerCase();
      const sectionEl = box.closest('[data-section="main-product"]') || box;
      const productHandle = (sectionEl.dataset.productHandle || '').toLowerCase();
      const template = (sectionEl.dataset.template || '').toLowerCase();
      return pathname.includes('501-club') ||
             document.body.classList.contains('template-product-501-club') ||
             template.includes('501-club') ||
             productHandle.includes('501-club') ||
             window.SF_IS_501_CLUB === true;
    };

    const getRechargeSellingPlan = () => {
      const rcWidget = document.querySelector('recharge-subscription-widget');
      if (rcWidget) {
        if (rcWidget.sellingPlanId && !isNaN(Number(rcWidget.sellingPlanId)) && Number(rcWidget.sellingPlanId) > 1000) {
          return Number(rcWidget.sellingPlanId);
        }
        if (rcWidget.shadowRoot) {
          const spSelect = rcWidget.shadowRoot.querySelector('select[name="selling_plan"]:not([disabled]), rc-plans-dropdown select, select');
          if (spSelect) {
            const optVal = String(spSelect.value || '').trim();
            if (!isNaN(Number(optVal)) && Number(optVal) > 1000) {
              return Number(optVal);
            }
          }
        }
      }

      // 1. Check Recharge app block in light DOM
      const rcBlock = box.querySelector('.pdp__block--app, [class*="recharge"]');
      if (rcBlock) {
        const spSelect = rcBlock.querySelector('select[name="selling_plan"]:not([disabled]), rc-plans-dropdown select, select');
        if (spSelect) {
          const selIdx = spSelect.selectedIndex >= 0 ? spSelect.selectedIndex : 0;
          const selectedOpt = spSelect.options ? spSelect.options[selIdx] : null;
          const optVal = String(spSelect.value || (selectedOpt ? selectedOpt.value : '')).trim();
          const optText = (selectedOpt ? (selectedOpt.text || selectedOpt.textContent || '') : '').toLowerCase();

          if (!isNaN(Number(optVal)) && Number(optVal) > 1000) {
            return Number(optVal);
          }

          const allPlans = (Array.isArray(window.sfSellingPlans) ? window.sfSellingPlans : [])
            .filter(p => p && p.id && !isNaN(Number(p.id)) && Number(p.id) > 1000);

          if (allPlans.length > 0) {
            if (optText.includes('4') || optVal.includes('4') || selIdx === 1) {
              const p4 = allPlans.find(p => /4\s*week|28\s*day|every\s*4|month/i.test(String(p.name || '') + ' ' + String(p.interval || '')));
              if (p4) return Number(p4.id);
              if (allPlans.length >= 2) return Number(allPlans[1].id);
            } else {
              const p2 = allPlans.find(p => /2\s*week|14\s*day|every\s*2/i.test(String(p.name || '') + ' ' + String(p.interval || '')));
              if (p2) return Number(p2.id);
              return Number(allPlans[0].id);
            }
          }
        }

        const rcInput = rcBlock.querySelector('input[name="selling_plan"]:not([disabled])');
        if (rcInput && rcInput.value && !isNaN(Number(rcInput.value)) && Number(rcInput.value) > 1000) {
          return Number(rcInput.value);
        }
      }

      // 2. Check general form input
      const spInput = box.querySelector('form [name="selling_plan"]:not([disabled])');
      if (spInput && spInput.value && !isNaN(Number(spInput.value)) && Number(spInput.value) > 1000) {
        return Number(spInput.value);
      }

      // 3. Fallback for 501 club PDP
      if (is501ClubPDP()) {
        const fallbackId = $('#pdpSellingPlanInput')?.value || window.SF_DEFAULT_SELLING_PLAN;
        if (fallbackId && !isNaN(Number(fallbackId)) && Number(fallbackId) > 1000) {
          return Number(fallbackId);
        }
      }

      return 694544433524;
    };

    const isRechargeSubscriptionSelected = () => {
      const rcWidget = document.querySelector('recharge-subscription-widget');
      if (rcWidget) {
        // 1. LitElement internal property
        if (rcWidget.purchaseOption === 'subscription') return true;
        if (rcWidget.purchaseOption === 'onetime') return false;

        // 2. Query inside shadow root
        if (rcWidget.shadowRoot) {
          const onetimeRadio = rcWidget.shadowRoot.querySelector('input[type="radio"][value="onetime"]');
          const subRadio = rcWidget.shadowRoot.querySelector('input[type="radio"][value="subscription"]');
          if (onetimeRadio && onetimeRadio.checked) return false;
          if (subRadio && subRadio.checked) return true;

          const onetimeOpt = rcWidget.shadowRoot.querySelector('.rc-selection__onetime, [part*="onetime-purchase-option"]');
          const subOpt = rcWidget.shadowRoot.querySelector('.rc-purchase-option__subscription, [part*="subscription-purchase-option"], .rc-selection_sub');

          if (onetimeOpt && (onetimeOpt.hasAttribute('rc-selected') || onetimeOpt.getAttribute('aria-checked') === 'true')) return false;
          if (subOpt && (subOpt.hasAttribute('rc-selected') || subOpt.getAttribute('aria-checked') === 'true')) return true;

          if (onetimeOpt && !onetimeOpt.hasAttribute('rc-not-selected')) return false;
          if (subOpt && !subOpt.hasAttribute('rc-not-selected')) return true;
        }
      }

      // 3. Query light DOM
      const onetimeRadio = document.querySelector('input[name="purchaseOption"][value="onetime"]:checked, input[name="selling_plan_purchase_option"][value="onetime"]:checked');
      if (onetimeRadio) return false;
      const subRadio = document.querySelector('input[name="purchaseOption"][value="subscription"]:checked, input[name="selling_plan_purchase_option"][value="subscription"]:checked');
      if (subRadio) return true;

      const spInput = document.querySelector('form [name="selling_plan"]:not([disabled])');
      if (spInput && spInput.value && !isNaN(Number(spInput.value)) && Number(spInput.value) > 1000) {
        return true;
      }

      if (is501ClubPDP()) {
        return false;
      }

      return true;
    };

    let isRendering = false;
    const render = () => {
      if (isRendering) return;
      isRendering = true;
      try {
        const hasVariants = window.SF_HAS_VARIANTS !== false && sizes.length > 0;
        const size = hasVariants ? (sizes.find(s => s.classList.contains('is-on')) || sizes[0]) : null;

        const currentVariantId = String(
          (!hasVariants ? ($('#pdpVariantId')?.value || window.SF_CURRENT_VARIANT_ID) : (size?.dataset.variantId || $('#pdpVariantId')?.value || window.SF_CURRENT_VARIANT_ID)) || ''
        ).replace(/\D/g, '');

        const currentProductId = String(window.SF_PRODUCT_ID || '').replace(/\D/g, '');
        const targetVariantId = String(window.SF_PROMO_CONFIG?.variantId || '').replace(/\D/g, '');
        const targetProductId = String(window.SF_PROMO_CONFIG?.productId || '').replace(/\D/g, '');

        // When user visits target product or selects target variant (or product doesn't require variant selection)
        const isPromoTarget = window.SF_PROMO_APP_SETTINGS?.enabled !== false &&
                              Boolean(targetVariantId) &&
                              (
                                (currentVariantId && currentVariantId === targetVariantId) ||
                                (!hasVariants && ((targetProductId && currentProductId === targetProductId) || currentVariantId === targetVariantId))
                              );


        const isPromoApplied = isPromoTarget && (window.SF_PROMO_VERIFIED || sessionStorage.getItem('sf_promo_verified') === 'true');

        // If product has no variants (e.g. Display Box 3ct or Countertop Display)
        if (!hasVariants) {
          const defaultPrice = (typeof window.SF_PRODUCT_PRICE === 'number' && window.SF_PRODUCT_PRICE > 0)
            ? window.SF_PRODUCT_PRICE
            : (parseFloat(box.dataset.price) || 11.99);

          const unitPrice = isPromoApplied && window.SF_PROMO_CONFIG?.promoPrice
            ? window.SF_PROMO_CONFIG.promoPrice
            : (isPromoTarget && window.SF_PROMO_CONFIG?.regularPrice ? window.SF_PROMO_CONFIG.regularPrice : defaultPrice);

          const totalPrice = unitPrice * currentQty;
          const priceStr = money(totalPrice);

          // Update main price in header if present
          const mainPriceEl = $('#pdpMainPrice');
          if (mainPriceEl) {
            if (isPromoApplied && window.SF_PROMO_CONFIG?.regularPrice) {
              const regTotal = window.SF_PROMO_CONFIG.regularPrice * currentQty;
              mainPriceEl.innerHTML = `<s style="color:#6d7175;font-weight:normal;margin-right:6px;">${money(regTotal)}</s><span style="color:#108043;font-weight:700;">${priceStr}</span>`;
            } else {
              if (mainPriceEl.textContent !== priceStr) mainPriceEl.textContent = priceStr;
            }
          }

          // Mirror price directly into the Add to Cart button
          if (mainBuyBtn) {
            const buyText = mainBuyBtn.querySelector('.pdp__buy-text');
            const btnLabel = `Add to cart · ${priceStr}`;
            if (buyText) {
              if (buyText.textContent !== btnLabel) buyText.textContent = btnLabel;
            } else {
              mainBuyBtn.innerHTML = `<span class="pdp__buy-text">${btnLabel}</span> <svg class="btn__arr" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="4" y1="12" x2="20" y2="12"></line><polyline points="14 6 20 12 14 18"></polyline></svg>`;
            }
          }

          // Mirror price into sticky mobile & desktop buy bar
          if (buybarPrice && buybarName) {
            const prodTitle = window.SF_PRODUCT_TITLE || 'Display Box 3ct';
            if (buybarName.textContent !== prodTitle) buybarName.textContent = prodTitle;
            const currentBuybarText = `${priceStr} · One-Time`;
            if (buybarPrice.textContent !== currentBuybarText) {
              buybarPrice.textContent = currentBuybarText;
            }
            const buybarBadge = $('#buybarBadge');
            if (buybarBadge) {
              buybarBadge.textContent = isPromoApplied ? 'PROMO APPLIED' : 'SPECIAL OFFER';
              buybarBadge.style.display = 'inline-flex';
            }
          }
          return;
        }

        const is501 = is501ClubPDP();
        if (!size) return;
        const count = parseInt(size.dataset.count, 10) || (is501 ? 24 : 1);

        // Cap quantity at 2 if 3-Pack Case is selected
        if (count === 3 && currentQty > 2) {
          currentQty = 2;
          if (qtyDisplay) qtyDisplay.textContent = '2';
          const qtyInput = $('#pdpQtyInput');
          if (qtyInput) qtyInput.value = '2';
        }

        const once = parseFloat(size.dataset.once) || (count === 1 ? 10.99 : (count === 3 ? 32.97 : (count === 24 ? 263.76 : (is501 ? 219.99 : 104.99))));
        const subPrice = parseFloat(size.dataset.sub) || (count === 24 ? 152.98 : (count === 3 ? 32.97 : (count === 1 ? 10.99 : 83.99)));
        const servings = size.dataset.servings || (count === 3 ? 6 : count * 2);
        const sizeName = size.dataset.name || (count === 1 ? 'Single Bottle' : (count === 3 ? '3-Pack Case' : (is501 ? '24-Pack (The 501 Club)' : `${count}-Pack Case`)));

        if (servingHint) {
          servingHint.textContent = `${servings} Servings (2 per bottle)`;
        }

        // 24-Pack Case dynamic toggles: 501 Club chip, Most Popular tag, and 24-Pack Perk message
        const is24Pack = count === 24 || is501;
        $$('#pdpChip501Club, .pdp__chip--501club').forEach(el => {
          el.style.display = is24Pack ? 'inline-flex' : 'none';
        });
        $$('#pdpPopularTag, .pdp__popular-tag').forEach(el => {
          el.style.display = is24Pack ? 'inline-flex' : 'none';
        });
        $$('#pdp24PackPerk, #pdp24PackPerkFallback').forEach(el => {
          el.style.display = is24Pack ? 'flex' : 'none';
        });

        // 3-Pack Case dynamic toggles: Special Offer chip, PROMO tag, and 3-Pack Perk message
        const is3ct = count === 3 && !is501;
        $$('#pdpChipOffer, .pdp__chip--offer').forEach(el => {
          el.style.display = is3ct ? 'inline-flex' : 'none';
        });
        $$('#pdpPromoTag, #pdpPromoTagFallback, .pdp__promo-tag').forEach(el => {
          el.style.display = is3ct ? 'inline-flex' : 'none';
        });
        $$('#pdp3PackPerk, #pdp3PackPerkFallback, .pdp__3pack-perk').forEach(el => {
          el.style.display = is3ct ? 'flex' : 'none';
        });

        // Single bottle & Display Box 3ct: hide subscription widget completely (they are one-time only)
        const rcBlock = box.querySelector('.pdp__block--app, [class*="recharge"]') || document.querySelector('.shopify-block.recharge-subscription-widget');
        if (rcBlock) {
          rcBlock.style.display = ((count === 1 || count === 3) && !is501) ? 'none' : '';
        }

        // Determine subscription status and unit price
        let isSub = false;
        let unitPrice = once;

        if (count === 1 && !is501) {
          isSub = false;
          unitPrice = (isPromoApplied && window.SF_PROMO_CONFIG?.promoPrice) ? window.SF_PROMO_CONFIG.promoPrice : (parseFloat(size.dataset.once) || 10.99);
        } else if (count === 3 && !is501) {
          isSub = false;
          // Set to promo discount price ($21.98)
          unitPrice = (window.SF_PROMO_CONFIG?.promoPrice || 21.98);
        } else if (is501) {
          isSub = isRechargeSubscriptionSelected();
          const v1Data = window.SF_VARIANTS?.v1;
          const once501 = parseFloat(v1Data?.price) || (size ? parseFloat(size.dataset.once) : 108.00);
          const sub501 = parseFloat(v1Data?.subPrice) || (size ? parseFloat(size.dataset.sub) : 97.20);
          unitPrice = isSub ? sub501 : once501;
        } else {
          isSub = isRechargeSubscriptionSelected();
          unitPrice = isSub ? subPrice : once;
        }

        const totalPrice = unitPrice * currentQty;
        const priceStr = money(totalPrice);

        const mainPriceEl = $('#pdpMainPrice');
        if (mainPriceEl) {
          if (count === 3 && !is501) {
            const regPrice = parseFloat(size.dataset.compareAt) || window.SF_PROMO_CONFIG?.regularPrice || 32.97;
            const regTotal = regPrice * currentQty;
            mainPriceEl.innerHTML = `<s style="color:#6d7175;font-weight:normal;margin-right:6px;">${money(regTotal)}</s><span style="color:#108043;font-weight:700;">${priceStr}</span>`;
          } else if (isPromoApplied) {
            const regPrice = window.SF_PROMO_CONFIG?.regularPrice || once;
            const regTotal = regPrice * currentQty;
            mainPriceEl.innerHTML = `<s style="color:#6d7175;font-weight:normal;margin-right:6px;">${money(regTotal)}</s><span style="color:#108043;font-weight:700;">${priceStr}</span>`;
          } else {
            mainPriceEl.textContent = priceStr;
          }
        }

        // Mirror price directly into the Add to Cart button
        if (mainBuyBtn) {
          const buyText = mainBuyBtn.querySelector('.pdp__buy-text');
          const btnLabel = `Add to cart · ${priceStr}`;

          if (buyText) {
            if (buyText.textContent !== btnLabel) buyText.textContent = btnLabel;
          } else {
            mainBuyBtn.innerHTML = `<span class="pdp__buy-text">${btnLabel}</span> <svg class="btn__arr" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="4" y1="12" x2="20" y2="12"></line><polyline points="14 6 20 12 14 18"></polyline></svg>`;
          }
        }

        // Mirror price into sticky mobile & desktop buy bar
        if (buybarPrice && buybarName) {
          const prodTitle = window.SF_PRODUCT_TITLE || (is501 ? 'Countertop Display: Calm Energy, 36ct' : `Super Feels · ${sizeName}`);
          buybarName.textContent = prodTitle;
          const subLabel = is501 ? 'Subscription' : 'Subscribe & Save';
          const currentBuybarText = `${priceStr} · ${(isSub && count !== 3) ? subLabel : 'One-Time'}`;
          if (buybarPrice.textContent !== currentBuybarText) {
            buybarPrice.textContent = currentBuybarText;
          }
          const buybarBadge = $('#buybarBadge');
          if (buybarBadge) {
            buybarBadge.textContent = count === 3 ? 'PROMO' : (count === 24 ? 'SAVE 42%' : (is501 ? 'SAVE 10%' : (count === 12 ? 'SAVE 20%' : 'POPULAR')));
            buybarBadge.style.display = 'inline-flex';
          }
          const buybarImg = $('#buybarImg');
          if (buybarImg) {
            const barSrc = (count === 1 && !is501) ? window.SF_IMG_SINGLE : window.SF_IMG_CASE;
            if (barSrc && buybarImg.src !== barSrc) buybarImg.src = barSrc;
          }
        }
      } finally {
        isRendering = false;
      }
    };

    // Promotional Popup Modal Elements & Controls
    const promoModal = $('#pdpPromoModal');
    const promoModalClose = $('#pdpPromoModalClose');
    const promoModalOverlay = $('#pdpPromoModalOverlay');
    const promoModalSkip = $('#pdpPromoModalSkipBtn');
    const promoModalEmail = $('#pdpPromoModalEmail');
    const promoModalBtn = $('#pdpPromoModalVerifyBtn');
    const promoModalMsg = $('#pdpPromoModalMsg');

    const openPromoModal = () => {
      if (!promoModal) return;
      promoModal.style.display = 'flex';
      promoModal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (promoModalEmail) {
        const savedEmail = sessionStorage.getItem('sf_promo_email') || window.SF_CUSTOMER_EMAIL || '';
        if (savedEmail && !promoModalEmail.value) promoModalEmail.value = savedEmail;
        setTimeout(() => promoModalEmail.focus(), 100);
      }
    };

    const closePromoModal = () => {
      if (!promoModal) return;
      promoModal.style.display = 'none';
      promoModal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    if (promoModalClose) promoModalClose.addEventListener('click', closePromoModal);
    if (promoModalOverlay) promoModalOverlay.addEventListener('click', closePromoModal);
    if (promoModalSkip) promoModalSkip.addEventListener('click', closePromoModal);

    try {
      const initialVariantParam = new URL(window.location.href).searchParams.get('variant');
      if (initialVariantParam) {
        const matchingSize = sizes.find(s => String(s.dataset.variantId).replace(/\D/g, '') === String(initialVariantParam).replace(/\D/g, ''));
        if (matchingSize) {
          sizes.forEach(o => {
            o.classList.remove('is-on');
            o.setAttribute('aria-checked', 'false');
          });
          matchingSize.classList.add('is-on');
          matchingSize.setAttribute('aria-checked', 'true');
          const hiddenId = $('#pdpVariantId') || $('input[name="id"]', box);
          if (hiddenId) hiddenId.value = initialVariantParam;
        }
      }
    } catch (e) {}

    sizes.forEach(s => s.addEventListener('click', () => {
      sizes.forEach(o => {
        o.classList.remove('is-on');
        o.setAttribute('aria-checked', 'false');
      });
      s.classList.add('is-on');
      s.setAttribute('aria-checked', 'true');

      // Update URL query parameter without full reload
      const variantId = s.dataset.variantId;
      if (variantId) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          window.history.replaceState({ variantId }, '', url.pathname + url.search + url.hash);
        } catch (e) {}

        // Update hidden product form inputs
        const hiddenId = $('#pdpVariantId') || $('input[name="id"]', box);
        if (hiddenId) {
          hiddenId.value = variantId;
          hiddenId.dispatchEvent(new Event('change', { bubbles: true }));
          hiddenId.dispatchEvent(new Event('input', { bubbles: true }));
        }

        // Emit standard Shopify & app events (for Recharge Subscriptions, Bold, etc.)
        const variantEventData = {
          variant: {
            id: variantId,
            available: s.dataset.available === 'true',
            price: Math.round(parseFloat(s.dataset.once || '0') * 100)
          },
          variantId
        };
        window.dispatchEvent(new CustomEvent('variant:changed', { detail: variantEventData }));
        document.dispatchEvent(new CustomEvent('variant:changed', { detail: variantEventData }));
        document.dispatchEvent(new CustomEvent('recharge:variant-changed', { detail: { variantId } }));
      }

      // Sync gallery media
      const targetMediaId = s.dataset.mediaId || (parseInt(s.dataset.count, 10) === 3 ? '56784197878132' : null);
      if (targetMediaId && window.showPdpMedia) {
        window.showPdpMedia(targetMediaId);
      }

      render();

      // If clicked variant is the promotional target and customer is not verified, open the promo modal
      const cleanVarId = String(variantId || '').replace(/\D/g, '');
      const cfgVarId = String(window.SF_PROMO_CONFIG?.variantId || '').replace(/\D/g, '');
      const isPromoTarget = window.SF_PROMO_APP_SETTINGS?.enabled !== false && cfgVarId && cleanVarId === cfgVarId;

      if (isPromoTarget && !window.SF_PROMO_VERIFIED && sessionStorage.getItem('sf_promo_verified') !== 'true') {
        openPromoModal();
      }
    }));

    // Ensure whole size cards (including badge and wrapper) trigger the option
    $$('.size__wrap', box).forEach(wrap => {
      wrap.addEventListener('click', (e) => {
        const btn = wrap.querySelector('.size__opt');
        if (btn && e.target !== btn && !btn.contains(e.target)) {
          btn.click();
        }
      });
    });

    // Direct Shadow DOM listener attachment to trigger button price update on click
    const attachRechargeShadowListeners = () => {
      const rcWidget = document.querySelector('recharge-subscription-widget');
      if (rcWidget && rcWidget.shadowRoot && !rcWidget.dataset.sfListenersAttached) {
        rcWidget.dataset.sfListenersAttached = 'true';
        ['click', 'pointerup', 'change', 'input'].forEach(evt => {
          rcWidget.shadowRoot.addEventListener(evt, () => {
            setTimeout(render, 10);
            setTimeout(render, 50);
            setTimeout(render, 150);
            setTimeout(render, 300);
          }, { passive: true });
        });
      }
    };

    // Global capture-phase listener to catch clicks inside shadow DOM via composedPath
    const onGlobalInteraction = (e) => {
      const path = (e.composedPath && e.composedPath()) || [e.target];
      const isRechargeRelated = path.some(el => {
        if (!el || !el.tagName) return false;
        const tag = el.tagName.toLowerCase();
        const cls = String(el.className || '');
        const part = String(el.getAttribute?.('part') || '');
        return tag.includes('recharge') || tag.startsWith('rc-') || cls.includes('rc-') || part.includes('rc-') || el.name === 'purchaseOption';
      });
      if (isRechargeRelated) {
        setTimeout(render, 10);
        setTimeout(render, 50);
        setTimeout(render, 150);
        setTimeout(render, 300);
      }
    };

    window.addEventListener('click', onGlobalInteraction, true);
    window.addEventListener('pointerup', onGlobalInteraction, true);
    window.addEventListener('change', onGlobalInteraction, true);
    window.addEventListener('input', onGlobalInteraction, true);

    // Initial triggers & polling safety net
    attachRechargeShadowListeners();
    const shadowPoll = setInterval(attachRechargeShadowListeners, 300);
    setTimeout(() => clearInterval(shadowPoll), 8000);

    // Observe ONLY the Recharge app block container
    const appBlockEl = $('.pdp__block--app') || $('[class*="recharge"]');
    if (appBlockEl && 'MutationObserver' in window) {
      const appMo = new MutationObserver(() => {
        if (!isRendering) render();
      });
      appMo.observe(appBlockEl, { attributes: true, subtree: true, childList: true });
    }

    // Capture Recharge / subscription events
    ['rc:initialized', 'rc:change', 'rc:selling-plan-change', 'recharge:variant-changed'].forEach(evt => {
      document.addEventListener(evt, () => setTimeout(render, 30));
      window.addEventListener(evt, () => setTimeout(render, 30));
    });

    // Fast initial sync intervals to catch Recharge async hydration
    setTimeout(render, 50);
    setTimeout(render, 150);
    setTimeout(render, 300);
    setTimeout(render, 600);
    setTimeout(render, 1200);

    // Continuous lightweight periodic sync so price in Add to Cart button stays accurate
    setInterval(render, 250);

    // Real-time Email Verification (Supports both Modal and Inline if present)
    const promoEmailInput = $('#pdpPromoEmail');
    const promoVerifyBtn = $('#pdpPromoVerifyBtn');
    const promoMsg = $('#pdpPromoMsg');

    const runPromoVerification = async (emailOverride, fromModal = false) => {
      const email = (emailOverride || (fromModal ? promoModalEmail?.value : promoEmailInput?.value) || promoModalEmail?.value || promoEmailInput?.value || '').trim().toLowerCase();
      const targetMsg = fromModal ? (promoModalMsg || promoMsg) : (promoMsg || promoModalMsg);
      const targetBtn = fromModal ? (promoModalBtn || promoVerifyBtn) : (promoVerifyBtn || promoModalBtn);

      if (!email || !email.includes('@') || !email.includes('.')) {
        if (targetMsg) {
          targetMsg.style.display = 'block';
          targetMsg.style.color = '#991b1b';
          targetMsg.style.background = '#fee2e2';
          targetMsg.style.border = '1px solid #fecaca';
          targetMsg.style.padding = '8px 12px';
          targetMsg.style.borderRadius = '6px';
          targetMsg.textContent = 'Please enter a valid email address.';
        }
        return false;
      }

      if (targetBtn) {
        targetBtn.textContent = 'Checking...';
        targetBtn.disabled = true;
      }

      try {
        const targetVariantId = String(window.SF_PROMO_CONFIG?.variantId || $('#pdpVariantId')?.value || '').replace(/\D/g, '');
        const res = await fetch(`${PROMO_WORKER_URL}/api/check-eligibility`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email, variantIds: [targetVariantId] })
        }).then(r => r.json());

        if (targetBtn) {
          targetBtn.textContent = fromModal ? 'Claim Discount' : 'Verify';
          targetBtn.disabled = false;
        }

        if (res.eligible) {
          if (targetMsg) {
            targetMsg.style.display = 'block';
            targetMsg.style.color = '#0d542b';
            targetMsg.style.background = '#eef7f0';
            targetMsg.style.border = '1.5px solid #c3e6cd';
            targetMsg.style.padding = '10px 14px';
            targetMsg.style.borderRadius = '8px';
            targetMsg.innerHTML = res.message || `🎉 Discount unlocked! Price updated to $8.00.`;
          }

          window.SF_PROMO_VERIFIED = true;
          window.SF_PROMO_DISCOUNT_CODE = res.discountCode;
          sessionStorage.setItem('sf_promo_verified', 'true');
          sessionStorage.setItem('sf_promo_email', email);
          sessionStorage.setItem('sf_promo_discount', res.discountCode);
          sessionStorage.setItem('sf_promo_variant_id', targetVariantId);
          localStorage.setItem('sf_promo_discount', res.discountCode);
          localStorage.setItem('sf_promo_email', email);

          render();

          if (fromModal) {
            setTimeout(() => {
              closePromoModal();
            }, 900);
          }
          return true;
        } else {
          if (targetMsg) {
            targetMsg.style.display = 'block';
            targetMsg.style.color = '#991b1b';
            targetMsg.style.background = '#fee2e2';
            targetMsg.style.border = '1.5px solid #fecaca';
            targetMsg.style.padding = '10px 14px';
            targetMsg.style.borderRadius = '8px';
            targetMsg.innerHTML = res.message || 'You have already reached the limit for this offer. You can still purchase at regular price.';
          }

          window.SF_PROMO_VERIFIED = false;
          window.SF_PROMO_DISCOUNT_CODE = null;
          sessionStorage.removeItem('sf_promo_verified');
          sessionStorage.removeItem('sf_promo_discount');
          localStorage.removeItem('sf_promo_discount');

          render();
          return false;
        }
      } catch (err) {
        if (targetBtn) {
          targetBtn.textContent = fromModal ? 'Claim Discount' : 'Verify';
          targetBtn.disabled = false;
        }
        if (targetMsg) {
          targetMsg.style.display = 'block';
          targetMsg.style.color = '#991b1b';
          targetMsg.textContent = 'Verification temporary error. Please try again.';
        }
        return false;
      }
    };

    if (promoModalBtn) {
      promoModalBtn.addEventListener('click', () => runPromoVerification(null, true));
    }
    if (promoModalEmail) {
      promoModalEmail.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          runPromoVerification(null, true);
        }
      });
    }

    if (promoVerifyBtn) {
      promoVerifyBtn.addEventListener('click', () => runPromoVerification(null, false));
    }
    if (promoEmailInput) {
      promoEmailInput.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          runPromoVerification(null, false);
        }
      });
      promoEmailInput.addEventListener('change', () => {
        if (promoEmailInput.value.includes('@')) {
          runPromoVerification(null, false);
        }
      });

      const savedEmail = sessionStorage.getItem('sf_promo_email') || window.SF_CUSTOMER_EMAIL;
      if (savedEmail) {
        promoEmailInput.value = savedEmail;
        if (sessionStorage.getItem('sf_promo_verified') === 'true') {
          runPromoVerification(savedEmail, false);
        }
      }
    }

    window.addEventListener('sf:promo-config-updated', () => render());

    // Hook Add to Cart button
    const handleAdd = e => {
      e?.preventDefault();
      const hasVariants = window.SF_HAS_VARIANTS !== false && sizes.length > 0;
      const size = hasVariants ? (sizes.find(s => s.classList.contains('is-on')) || sizes[0]) : null;
      const targetVariantId = size?.dataset.variantId || $('#pdpVariantId')?.value || window.SF_CURRENT_VARIANT_ID;

      // Handle single-variant products (e.g. Display Box 3ct)
      if (!hasVariants) {
        const prodTitle = window.SF_PRODUCT_TITLE || 'Display Box 3ct';
        const isVerified = window.SF_PROMO_VERIFIED || sessionStorage.getItem('sf_promo_verified') === 'true';
        const defaultPrice = (typeof window.SF_PRODUCT_PRICE === 'number' && window.SF_PRODUCT_PRICE > 0)
          ? window.SF_PRODUCT_PRICE
          : (parseFloat(box.dataset.price) || 11.99);
        const unitPrice = (isVerified && window.SF_PROMO_CONFIG?.promoPrice)
          ? window.SF_PROMO_CONFIG.promoPrice
          : (window.SF_PROMO_CONFIG?.regularPrice || defaultPrice);
        const prodImg = window.SF_IMG_FEATURED || window.SF_IMG_SINGLE || '';

        window.sfAddToCart({
          id: `product-${targetVariantId}`,
          variantId: targetVariantId,
          qty: currentQty || 1,
          isSub: false,
          sellingPlan: null,
          title: prodTitle,
          plan: 'One-Time Purchase',
          price: unitPrice,
          img: prodImg
        });
        return;
      }

      const is501 = is501ClubPDP();
      const count = parseInt(size?.dataset.count || (is501 ? '24' : '1'), 10);

      // 1. Single Bottle on standard product: ALWAYS One-Time ($10.99 regular, or $8.00 if promo verified)
      if (count === 1 && !is501) {
        const isVerified = window.SF_PROMO_VERIFIED || sessionStorage.getItem('sf_promo_verified') === 'true';
        const itemPrice = (isVerified && window.SF_PROMO_CONFIG?.promoPrice) ? window.SF_PROMO_CONFIG.promoPrice : 10.99;
        window.sfAddToCart({
          id: 'super-feels-1-once',
          variantId: targetVariantId,
          qty: currentQty || 1,
          isSub: false,
          sellingPlan: null,
          title: 'Super Feels (Single Bottle)',
          plan: 'One-Time Purchase',
          price: itemPrice,
          img: window.SF_IMG_SINGLE || ''
        });
        return;
      }

      // 1b. 3-Pack Case on standard product: ALWAYS One-Time, never subscription, max 2
      if (count === 3 && !is501) {
        const promoPrice = window.SF_PROMO_CONFIG?.promoPrice || 21.98;

        window.sfAddToCart({
          id: 'super-feels-3-once',
          variantId: targetVariantId,
          qty: Math.min(currentQty || 1, 2),
          isSub: false,
          sellingPlan: null,
          title: 'Super Feels (3-Pack Case)',
          plan: 'One-Time Purchase',
          price: promoPrice,
          img: window.SF_IMG_CASE || window.SF_IMG_FEATURED || window.SF_IMG_SINGLE || ''
        });
        return;
      }

      // 2. 501 Club product page
      if (is501) {
        const isSub = isRechargeSubscriptionSelected();
        const v1Data = window.SF_VARIANTS?.v1;
        const once501 = parseFloat(v1Data?.price) || (size ? parseFloat(size.dataset.once) : 108.00);
        const sub501 = parseFloat(v1Data?.subPrice) || (size ? parseFloat(size.dataset.sub) : 97.20);
        const unitPrice = isSub ? sub501 : once501;
        const activeSellingPlan = isSub ? (getRechargeSellingPlan() || window.SF_DEFAULT_SELLING_PLAN) : null;
        const prodTitle = window.SF_PRODUCT_TITLE || 'Countertop Display: Calm Energy, 36ct';
        const planTitle = isSub ? 'Subscription' : 'One-Time Purchase';

        window.sfAddToCart({
          id: `501-club-${isSub ? 'sub' : 'once'}`,
          variantId: targetVariantId,
          qty: currentQty || 1,
          isSub: isSub,
          sellingPlan: activeSellingPlan,
          title: prodTitle,
          plan: planTitle,
          price: unitPrice,
          img: window.SF_IMG_CASE || ''
        });
        return;
      }

      // 3. Multi-pack Case (12-Pack or 24-Pack) on standard product
      const isSub = isRechargeSubscriptionSelected();
      const once = parseFloat(size?.dataset.once) || (count === 24 ? 263.76 : 104.99);
      const subPrice = parseFloat(size?.dataset.sub) || (count === 24 ? 152.98 : 83.99);
      const unitPrice = isSub ? subPrice : once;
      const discountText = count === 24 ? '42% OFF' : '20% OFF';
      const planTitle = isSub ? `Subscribe & Save · ${discountText}` : `${count}-Pack Case · One-Time`;
      const activeSellingPlan = isSub ? (getRechargeSellingPlan() || 694544433524) : null;

      window.sfAddToCart({
        id: `super-feels-${count}-${isSub ? 'sub' : 'once'}`,
        variantId: targetVariantId,
        qty: currentQty || 1,
        isSub: isSub,
        sellingPlan: activeSellingPlan,
        title: `Super Feels (${count}-Pack Case)`,
        plan: planTitle,
        price: unitPrice,
        img: window.SF_IMG_CASE || ''
      });
    };

    if (mainBuyBtn) mainBuyBtn.addEventListener('click', handleAdd);
    if (buybarBtn) buybarBtn.addEventListener('click', handleAdd);

    // Clicking buybar info scrolls smoothly back up to buybox
    if (bar) {
      const buybarInfo = $('.buybar__t', bar);
      if (buybarInfo) {
        buybarInfo.style.cursor = 'pointer';
        buybarInfo.addEventListener('click', () => {
          box.scrollIntoView({ behavior: 'smooth' });
        });
      }

      /* Sticky buy bar scroll trigger:
         - When user scrolls on mobile/desktop, if the main buy button (#pdpAddToCart) is NOT visible on screen, SHOW the sticky bar.
         - When #pdpAddToCart is visible on screen, HIDE the sticky bar.
         - When user scrolls further past the button, SHOW the sticky bar again.
      */
      const cta = mainBuyBtn || box;
      const sync = () => {
        if (!cta) return;
        const rect = cta.getBoundingClientRect();
        // Check if CTA button is within viewport bounds
        const isCtaVisible = rect.top < (window.innerHeight - 30) && rect.bottom > 30;
        
        // Show sticky bar whenever CTA is not on screen AND user has initiated scrolling
        const hasScrolled = window.scrollY > 30;
        const showSticky = !isCtaVisible && hasScrolled;
        
        bar.classList.toggle('is-on', showSticky);
        bar.setAttribute('aria-hidden', String(!showSticky));
      };

      window.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    }

    // Check URL query parameter for pre-selected variant (e.g., ?variant=12345678)
    try {
      const params = new URLSearchParams(window.location.search);
      const v = (params.get('variant') || params.get('size') || params.get('count') || '').trim().toLowerCase();
      if (v) {
        let matched = sizes.find(s => s.dataset.variantId && s.dataset.variantId.toString().toLowerCase() === v);
        if (!matched) {
          if (v === '56157808099700' || v === '24' || v.includes('24')) {
            matched = sizes.find(s => s.dataset.count === '24');
          } else if (v === '12' || v.includes('12') || v.includes('case') || v.includes('pack')) {
            matched = sizes.find(s => s.dataset.count === '12');
          } else if (v === '3' || v.includes('3') || v.includes('display')) {
            matched = sizes.find(s => s.dataset.count === '3');
          } else if (v === '1' || v.includes('single')) {
            matched = sizes.find(s => s.dataset.count === '1');
          }
        }
        if (matched) {
          sizes.forEach(o => {
            o.classList.remove('is-on');
            o.setAttribute('aria-checked', 'false');
          });
          matched.classList.add('is-on');
          matched.setAttribute('aria-checked', 'true');
        }
      }
    } catch (e) {}

    // Ensure active size has form input and media synchronized
    const activeSize = sizes.find(s => s.classList.contains('is-on')) || sizes[0];
    if (activeSize) {
      const activeMediaId = activeSize.dataset.mediaId || (parseInt(activeSize.dataset.count, 10) === 3 ? '56784197878132' : null);
      if (activeMediaId && window.showPdpMedia) {
        window.showPdpMedia(activeMediaId);
      }
      const hiddenId = $('#pdpVariantId') || $('input[name="id"]', box);
      if (hiddenId && activeSize.dataset.variantId) {
        hiddenId.value = activeSize.dataset.variantId;
      }
    }

    render();
  })();

  /* ---------------------------------------------------------
     PDP GALLERY, SWIPEABLE MOBILE CAROUSEL & ZOOM MODAL
     --------------------------------------------------------- */
  (() => {
    const gal = $('.pdp__gal');
    if (!gal) return;
    const track = $('#pdpTrack', gal);
    const slides = $$('.pdp__slide', gal);
    const thumbs = $$('.pdp__thumb', gal);
    const dots = $$('.pdp__dot', gal);
    const zoomModal = $('#zoomModal');
    const zoomImg = $('#zoomImg');
    const zoomClose = $('#zoomClose');
    const zoomBackdrop = $('#zoomBackdrop');

    let activeIndex = Math.max(0, slides.findIndex(s => s.classList.contains('is-active')));

    const setActive = (idx, scrollTrack = false) => {
      activeIndex = Math.max(0, Math.min(idx, slides.length - 1));

      // update desktop slides
      slides.forEach((s, i) => s.classList.toggle('is-active', i === activeIndex));

      // update thumbs
      thumbs.forEach((t, i) => t.classList.toggle('is-on', i === activeIndex));

      // update dots
      dots.forEach((d, i) => {
        const isOn = i === activeIndex;
        d.classList.toggle('is-on', isOn);
        d.setAttribute('aria-selected', String(isOn));
      });

      // scroll track on mobile if requested
      if (scrollTrack && track && slides[activeIndex]) {
        const slide = slides[activeIndex];
        const leftPos = slide.offsetLeft;
        track.scrollTo({ left: leftPos, behavior: 'smooth' });
      }
    };

    // Desktop thumbnails click
    thumbs.forEach((t, idx) => t.addEventListener('click', () => {
      setActive(idx, true);
    }));

    // Mobile dot indicators click
    dots.forEach((d, idx) => d.addEventListener('click', () => {
      setActive(idx, true);
    }));

    // Mobile swipe listener on scroll snapping track
    if (track) {
      let scrollTimer = null;
      track.addEventListener('scroll', () => {
        if (!track.clientWidth) return;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const firstSlide = slides[0];
          const slideW = firstSlide ? (firstSlide.offsetWidth + 10) : (track.clientWidth * 0.8);
          const idx = Math.round(track.scrollLeft / slideW);
          if (idx !== activeIndex && idx >= 0 && idx < slides.length) {
            setActive(idx, false);
          }
        }, 35);
      }, { passive: true });
    }

    // Zoom modal integration
    if (track && zoomModal && zoomImg) {
      track.addEventListener('click', (e) => {
        if (e.target.closest('.pdp__dots') || e.target.closest('.pdp__thumbs')) return;
        const curSlide = slides[activeIndex] || slides[0];
        const img = curSlide ? $('img', curSlide) : null;
        if (img) zoomImg.src = img.src;
        zoomModal.hidden = false;
        zoomModal.setAttribute('aria-hidden', 'false');
      });

      const closeZoom = () => {
        zoomModal.hidden = true;
        zoomModal.setAttribute('aria-hidden', 'true');
      };
      if (zoomClose) zoomClose.addEventListener('click', (e) => { e.stopPropagation(); closeZoom(); });
      if (zoomBackdrop) zoomBackdrop.addEventListener('click', closeZoom);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !zoomModal.hidden) closeZoom();
      });
    }
  })();

  /* ---------------------------------------------------------
     FAQ CATEGORY FILTER
     --------------------------------------------------------- */
  (() => {
    const bar = $('.faq__cats');
    if (!bar) return;
    $$('.faq__cat', bar).forEach(btn => btn.addEventListener('click', () => {
      $$('.faq__cat', bar).forEach(b => b.classList.remove('is-on'));
      btn.classList.add('is-on');
      const cat = btn.dataset.cat;
      $$('[data-faq-group]').forEach(g => {
        g.hidden = cat !== 'all' && g.dataset.faqGroup !== cat;
      });
    }));
  })();

  /* ---------------------------------------------------------
     STORE LOCATOR
     Interactive Map powered by Map API & dynamic store blocks.
     Handled directly in sections/store-locator.liquid
     --------------------------------------------------------- */

  /* ---------------------------------------------------------
     REVEAL ON SCROLL — one observer, opacity only.
     --------------------------------------------------------- */
  (() => {
    if (REDUCED || !('IntersectionObserver' in window)) return;
    const targets = $$('[data-reveal]');
    if (!targets.length) return;
    targets.forEach(t => { t.style.opacity = '0'; t.style.transform = 'translateY(16px)'; });
    const io = new IntersectionObserver(entries => {
      entries.filter(en => en.isIntersecting).forEach((en, i) => {
        const el = en.target;
        // stagger co-visible items so a section builds rather than pops
        const d = Math.min(i, 5) * 70;
        el.style.transition = `opacity .7s cubic-bezier(.16,1,.3,1) ${d}ms, transform .7s cubic-bezier(.16,1,.3,1) ${d}ms`;
        el.style.opacity = '1';
        el.style.transform = 'none';
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    targets.forEach(t => io.observe(t));
  })();

  /* ---------------------------------------------------------
     INTERACTIVE INGREDIENTS ORBIT
     --------------------------------------------------------- */
  (() => {
    const dots = $$('.ing__dot'), panes = $$('.ing__pane');
    if (!dots.length || !panes.length) return;

    const select = (key) => {
      dots.forEach(d => {
        const on = d.dataset.ing === key;
        d.classList.toggle('is-on', on);
        d.setAttribute('aria-selected', String(on));
      });
      panes.forEach(p => {
        p.classList.toggle('is-on', p.dataset.ing === key);
      });
    };

    dots.forEach(d => {
      const key = d.dataset.ing;
      d.addEventListener('click', () => select(key));
      d.addEventListener('mouseenter', () => select(key));
      d.addEventListener('focus', () => select(key));
    });
  })();


  /* ---------------------------------------------------------
     FAQ ACCORDION — Smooth single-open animation
     --------------------------------------------------------- */
  (() => {
    const containers = $$('.faq__list, .faq');
    if (!containers.length) return;

    containers.forEach(container => {
      const detailsList = $$('details', container);
      detailsList.forEach(details => {
        const summary = $('summary', details);
        const body = $('.faq__b', details);
        if (!summary || !body) return;

        summary.addEventListener('click', (e) => {
          e.preventDefault();
          const isOpen = details.hasAttribute('open');

          if (isOpen) {
            // Smoothly close
            body.style.maxHeight = `${body.scrollHeight}px`;
            body.style.opacity = '1';
            requestAnimationFrame(() => {
              body.style.maxHeight = '0px';
              body.style.opacity = '0';
              body.style.paddingBottom = '0px';
            });
            setTimeout(() => {
              details.removeAttribute('open');
              body.style.maxHeight = '';
              body.style.opacity = '';
              body.style.paddingBottom = '';
            }, 320);
          } else {
            // Close all other open details in this container
            detailsList.forEach(other => {
              if (other !== details && other.hasAttribute('open')) {
                const otherBody = $('.faq__b', other);
                if (otherBody) {
                  otherBody.style.maxHeight = `${otherBody.scrollHeight}px`;
                  otherBody.style.opacity = '1';
                  requestAnimationFrame(() => {
                    otherBody.style.maxHeight = '0px';
                    otherBody.style.opacity = '0';
                    otherBody.style.paddingBottom = '0px';
                  });
                  setTimeout(() => {
                    other.removeAttribute('open');
                    otherBody.style.maxHeight = '';
                    otherBody.style.opacity = '';
                    otherBody.style.paddingBottom = '';
                  }, 320);
                } else {
                  other.removeAttribute('open');
                }
              }
            });

            // Smoothly open
            details.setAttribute('open', '');
            body.style.maxHeight = '0px';
            body.style.opacity = '0';
            body.style.paddingBottom = '0px';
            requestAnimationFrame(() => {
              body.style.maxHeight = `${body.scrollHeight + 30}px`;
              body.style.opacity = '1';
              body.style.paddingBottom = '22px';
            });
            setTimeout(() => {
              body.style.maxHeight = '';
              body.style.opacity = '';
              body.style.paddingBottom = '';
            }, 340);
          }
        });
      });
    });
  })();

  /* ---------------------------------------------------------
     WHOLESALE / RETAILER PARTNER APPLICATION FORM HANDLER
     --------------------------------------------------------- */
  (() => {
    const wsForm = $('#wholesaleForm');
    if (!wsForm) return;
    wsForm.addEventListener('submit', e => {
      e.preventDefault();
      const fields = $('#formFields');
      const success = $('#formSuccess');
      if (fields) fields.style.display = 'none';
      if (success) {
        success.style.display = 'block';
        success.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  })();

  /* ---------------------------------------------------------
     LIVE VIEWER COUNTER (FOMO) - HERO & PDP
     --------------------------------------------------------- */
  (() => {
    const heroEl = document.getElementById('heroViewerCount');
    const pdpEl = document.getElementById('pdpViewerCount');
    if (!heroEl && !pdpEl) return;

    // Baseline in the middle of 250 - 350 range
    let currentCount = Math.floor(Math.random() * 60) + 270; // 270 - 330
    if (heroEl) heroEl.textContent = currentCount + '+';
    if (pdpEl) pdpEl.textContent = currentCount + '+';

    function updateViewers() {
      // Natural fluctuation (+1 to +6 or -1 to -5)
      let change = Math.floor(Math.random() * 11) - 5;
      
      // Keep natural drift towards center of 250-350
      if (currentCount <= 265) change = Math.abs(change) + 1;
      if (currentCount >= 335) change = -Math.abs(change) - 1;

      currentCount = Math.max(250, Math.min(350, currentCount + change));

      [heroEl, pdpEl].forEach(el => {
        if (!el) return;
        el.style.opacity = '0.3';
        el.style.transform = 'translateY(-2px)';
        setTimeout(() => {
          el.textContent = currentCount + '+';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }, 250);
      });

      // Random natural intervals between 3.5s and 6.5s
      const nextDelay = Math.floor(Math.random() * 3000) + 3500;
      setTimeout(updateViewers, nextDelay);
    }

    setTimeout(updateViewers, 2500);
  })();


  /* ---------------------------------------------------------
     SEARCH — header icon opens a panel; predictive results come
     from Shopify's /search/suggest.json as you type.
     Debounced, with the previous request aborted, and answers
     cached per query for the life of the page.
     Combobox semantics follow ARIA 1.2: focus stays in the input
     and aria-activedescendant points at the highlighted row.
     --------------------------------------------------------- */
  (() => {
    const panel = $('#searchPanel');
    const btn = $('#searchBtn');
    if (!panel || !btn) return;

    const input = $('#searchInput');
    const results = $('#searchResults');
    const status = $('#searchStatus');
    const form = $('#searchForm');
    const clearBtn = $('#searchClear');
    const closeBtn = $('#searchClose');
    const overlay = $('#searchOverlay');

    const cfg = window.SF_SEARCH || {};
    const TYPES = cfg.types || 'product,page';
    const LIMIT = cfg.limit || 4;
    const DEBOUNCE = cfg.debounce == null ? 250 : cfg.debounce;
    const MIN = cfg.minChars || 2;
    const PREDICTIVE = cfg.predictive !== false;
    const SHOW_IMAGE = cfg.showImage !== false;
    const SHOW_PRICE = cfg.showPrice !== false;
    const FIELDS = 'title,product_type,variants.title,vendor,tag,body';

    const isSheet = () => matchMedia('(max-width: 720px)').matches;

    const cache = new Map();
    let timer = null;
    let controller = null;
    let rows = [];
    let active = -1;

    const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g,
      c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    // Highlight the typed term inside a result title. Escaped first, so the
    // only markup that reaches innerHTML is the <mark> we add here.
    const mark = (text, q) => {
      const safe = esc(text);
      const needle = q.trim();
      if (!needle) return safe;
      const i = safe.toLowerCase().indexOf(esc(needle).toLowerCase());
      if (i < 0) return safe;
      const len = esc(needle).length;
      return safe.slice(0, i) + '<mark>' + safe.slice(i, i + len) + '</mark>' + safe.slice(i + len);
    };

    const money = cents => {
      const n = Number(cents);
      if (!isFinite(n)) return '';
      return '$' + (n / 100).toFixed(2);
    };

    // Shopify appends tracking params to suggestion URLs; keep them, they feed
    // the store's search analytics.
    const quickLinks = () => {
      const links = cfg.quickLinks || [];
      if (!links.length) return '';
      return '<ul class="srch__quick">' + links.map(l =>
        `<li><a href="${esc(l.url)}">${esc(l.label)}</a></li>`).join('') + '</ul>';
    };

    const setStatus = msg => { if (status) status.textContent = msg; };

    const collectRows = () => {
      rows = $$('.srch__row, .srch__all', results);
      rows.forEach((r, i) => {
        r.setAttribute('role', 'option');
        r.id = r.id || `searchOpt${i}`;
        r.setAttribute('aria-selected', 'false');
      });
      active = -1;
      input.setAttribute('aria-expanded', rows.length ? 'true' : 'false');
      input.removeAttribute('aria-activedescendant');
    };

    const setActive = i => {
      if (!rows.length) return;
      if (active >= 0 && rows[active]) {
        rows[active].classList.remove('is-active');
        rows[active].setAttribute('aria-selected', 'false');
      }
      active = (i + rows.length) % rows.length;
      const el = rows[active];
      el.classList.add('is-active');
      el.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    };

    const renderIdle = () => {
      results.innerHTML = quickLinks();
      collectRows();
      setStatus('');
    };

    const renderEmpty = q => {
      results.innerHTML =
        `<div class="srch__empty"><b>No results for &ldquo;${esc(q)}&rdquo;</b>` +
        `Try a product name, an ingredient, or something from the label ` +
        `&mdash; &ldquo;kava&rdquo;, &ldquo;lab results&rdquo;, &ldquo;501 Club&rdquo;.</div>` +
        quickLinks();
      collectRows();
      setStatus(`No results for ${q}`);
    };

    const render = (data, q) => {
      const products = (data.products || []).slice(0, LIMIT);
      const pages = (data.pages || []).slice(0, LIMIT);
      const articles = (data.articles || []).slice(0, LIMIT);
      const total = products.length + pages.length + articles.length;
      if (!total) return renderEmpty(q);

      let html = '';

      if (products.length) {
        html += '<div class="srch__group"><p class="srch__grouph">Products</p>';
        products.forEach(p => {
          const img = SHOW_IMAGE && p.featured_image && p.featured_image.url
            ? `<img class="srch__thumb" src="${esc(p.featured_image.url)}" alt="" width="46" height="46" loading="lazy">`
            : '';
          const price = SHOW_PRICE && p.price_min != null
            ? `<span class="srch__price">${esc(money(p.price_min))}</span>` : '';
          html += `<a class="srch__row" href="${esc(p.url)}">${img}` +
            `<span class="srch__t"><span class="srch__name">${mark(p.title, q)}</span>` +
            (p.type ? `<span class="srch__meta">${esc(p.type)}</span>` : '') +
            `</span>${price}</a>`;
        });
        html += '</div>';
      }

      const linkGroup = (items, label) => {
        if (!items.length) return '';
        let h = `<div class="srch__group"><p class="srch__grouph">${label}</p>`;
        items.forEach(it => {
          const path = String(it.url || '').split('?')[0];
          h += `<a class="srch__row" href="${esc(it.url)}">` +
            `<span class="srch__t"><span class="srch__name">${mark(it.title, q)}</span>` +
            `<span class="srch__meta">${esc(path)}</span></span></a>`;
        });
        return h + '</div>';
      };

      html += linkGroup(pages, 'Pages');
      html += linkGroup(articles, 'Journal');

      html += `<a class="srch__all" href="/search?q=${encodeURIComponent(q)}">` +
        `<span>See all results for &ldquo;${esc(q)}&rdquo;</span><span aria-hidden="true">&rarr;</span></a>`;

      results.innerHTML = html;
      collectRows();
      setStatus(`${total} result${total === 1 ? '' : 's'} for ${q}`);
    };

    const query = async q => {
      if (cache.has(q)) return render(cache.get(q), q);
      if (controller) controller.abort();
      controller = new AbortController();
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}` +
        `&resources[type]=${encodeURIComponent(TYPES)}` +
        `&resources[limit]=${LIMIT}` +
        `&resources[options][fields]=${encodeURIComponent(FIELDS)}`;
      try {
        const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(res.status);
        const data = (await res.json()).resources.results;
        cache.set(q, data);
        render(data, q);
      } catch (err) {
        if (err.name === 'AbortError') return;   // superseded by a newer keystroke
        results.innerHTML =
          '<div class="srch__empty"><b>Search is unavailable right now</b>' +
          'Press Enter to run a full search instead.</div>';
        collectRows();
        setStatus('Search is unavailable');
      }
    };

    const onInput = () => {
      const q = input.value.trim();
      if (clearBtn) clearBtn.hidden = !q;
      clearTimeout(timer);
      if (!PREDICTIVE) return;
      if (q.length < MIN) {
        if (controller) controller.abort();
        return renderIdle();
      }
      timer = setTimeout(() => query(q), DEBOUNCE);
    };

    const open = () => {
      if (!panel.hidden) return;
      // One overlay at a time. Click each drawer's own close button rather than
      // reaching into its state, so their aria and scroll-lock stay correct.
      const nav = $('#drawer');
      const cart = $('#cartDrawer');
      if (nav && !nav.hidden) $('#drawerCloseBtn')?.click();
      if (cart && !cart.hidden) $('#cartCloseBtn')?.click();
      panel.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      // Only the mobile sheet locks scrolling; on desktop this is a dropdown
      // and the page behind it stays usable, exactly like the mega menu.
      if (isSheet()) document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => panel.classList.add('is-open'));
      renderIdle();
      setTimeout(() => input.focus(), 60);
    };

    const close = () => {
      if (panel.hidden) return;
      panel.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      clearTimeout(timer);
      if (controller) controller.abort();
      setTimeout(() => { panel.hidden = true; }, 300);
      btn.focus();
    };

    btn.addEventListener('click', () => (panel.hidden ? open() : close()));
    closeBtn?.addEventListener('click', close);
    overlay?.addEventListener('click', close);
    input.addEventListener('input', onInput);

    clearBtn?.addEventListener('click', () => {
      input.value = '';
      clearBtn.hidden = true;
      renderIdle();
      input.focus();
    });

    form.addEventListener('submit', e => {
      // an arrowed-to row wins over submitting the raw query
      if (active >= 0 && rows[active]) {
        e.preventDefault();
        rows[active].click();
      }
    });

    input.addEventListener('keydown', e => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'Home' && rows.length) { e.preventDefault(); setActive(0); }
      else if (e.key === 'End' && rows.length) { e.preventDefault(); setActive(rows.length - 1); }
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !panel.hidden) close();
    });
  })();

  /* ---------------------------------------------------------
     CART PAGE AJAX QUANTITY & REMOVE HANDLER
     --------------------------------------------------------- */
  document.addEventListener('click', async e => {
    const btn = e.target.closest('.cart-page__qty-btn, .cart-page__del-btn');
    if (!btn) return;
    e.preventDefault();
    const key = btn.dataset.key;
    const qty = parseInt(btn.dataset.qty ?? '0', 10);
    if (!key) {
      if (btn.href) window.location.href = btn.href;
      return;
    }
    try {
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
      });
      window.location.reload();
    } catch (err) {
      if (btn.href) window.location.href = btn.href;
    }
  });

  /* ---------------------------------------------------------
     CART PAGE PROMO DISPLAY & AUTO CHECKOUT ENHANCEMENT
     --------------------------------------------------------- */
  (() => {
    if (!window.location.pathname.startsWith('/cart')) return;

    const enhanceCartPage = () => {
      const isVerified = window.SF_PROMO_VERIFIED || sessionStorage.getItem('sf_promo_verified') === 'true' || localStorage.getItem('sf_promo_discount');
      if (!isVerified || !window.SF_PROMO_CONFIG) return;

      const promoVariantId = String(window.SF_PROMO_CONFIG.variantId || '').replace(/\D/g, '');
      const regPrice = Number(window.SF_PROMO_CONFIG.regularPrice || 108.00);
      const promoPrice = Number(window.SF_PROMO_CONFIG.promoPrice || 97.01);
      const discountPerUnit = regPrice - promoPrice;

      const items = $$('.cart-page__items .cart-item, .cart-item[data-line-key]');
      let foundPromo = false;
      let totalDiscount = 0;

      items.forEach(item => {
        const itemTitle = (item.querySelector('.cart-item__title')?.textContent || '').toLowerCase();
        const priceEl = item.querySelector('.cart-item__price');
        const qtyEl = item.querySelector('.cart-item__qty');
        const qty = parseInt(qtyEl?.textContent || '1', 10);

        // Check if item corresponds to promo variant or product
        const isTargetItem = itemTitle.includes('countertop') || 
                             itemTitle.includes('36ct') || 
                             (item.dataset.lineKey && item.dataset.lineKey.includes(promoVariantId));

        if (isTargetItem) {
          foundPromo = true;
          totalDiscount += discountPerUnit * qty;

          if (priceEl && !priceEl.dataset.promoEnhanced) {
            priceEl.dataset.promoEnhanced = 'true';
            priceEl.innerHTML = `<s style="color:#6d7175;font-weight:normal;margin-right:6px;">$${(regPrice * qty).toFixed(2)}</s><span style="color:#108043;font-weight:700;">$${(promoPrice * qty).toFixed(2)}</span>`;

            const infoEl = item.querySelector('.cart-item__info');
            if (infoEl && !infoEl.querySelector('.promo-badge-cart')) {
              const badge = document.createElement('div');
              badge.className = 'promo-badge-cart';
              badge.style.cssText = 'font-size:12px;font-weight:700;color:#108043;margin-top:4px;';
              badge.textContent = `Special Offer Applied (-$${(discountPerUnit * qty).toFixed(2)})`;
              infoEl.appendChild(badge);
            }
          }
        }
      });

      if (foundPromo && totalDiscount > 0) {
        const subtotalEl = $('.cart-page__footer b, .cart__subtotal-value');
        if (subtotalEl && !subtotalEl.dataset.promoEnhanced) {
          subtotalEl.dataset.promoEnhanced = 'true';
          const currentText = subtotalEl.textContent.replace(/[^0-9.]/g, '');
          const origTotal = parseFloat(currentText) || regPrice;
          const discountedTotal = Math.max(0, origTotal - totalDiscount);
          subtotalEl.innerHTML = `<s style="color:#8F97A3;font-size:16px;font-weight:normal;margin-right:6px;">$${origTotal.toFixed(2)}</s><span style="color:#108043;">$${discountedTotal.toFixed(2)}</span>`;
        }
      }
    };

    enhanceCartPage();
    window.addEventListener('sf:promo-config-updated', enhanceCartPage);

    // Auto apply discount on cart page checkout
    const cartCheckoutBtn = $('button[name="checkout"]', $('#cartForm')) || $('.cart-page__footer button[type="submit"]');
    if (cartCheckoutBtn) {
      cartCheckoutBtn.addEventListener('click', e => {
        const promoCode = window.SF_PROMO_DISCOUNT_CODE || localStorage.getItem('sf_promo_discount') || sessionStorage.getItem('sf_promo_discount');
        if (promoCode) {
          e.preventDefault();
          window.location.href = `/discount/${encodeURIComponent(promoCode)}?redirect=/checkout`;
        }
      });
    }
  })();
})();


